import React, { useState } from "react"
import PropTypes from "prop-types"

import logIFTTTNextAction from "app/lib/ifttt_next_log_action"
import Steps from "./steps"
import ServiceSelection from "./service_selection"
import Upsell from "./upsell"
import RecommendedApplets from "./recommended_applets"
import { submitChannels } from "./api"

import { useCookies } from "react-cookie"
import { proDetailsPopupCookieName } from "shared/components/pro_details_popup"
import { TrialSuccessMessageCopies } from "app/components/subscriptions/trial/utils/trial_success_message_copies"

const OnboardingRecommendations = ({
  channels,
  iftttSvgUrl,
  proBadgeSvgUrl,
  recommendedApplets,
  trialConfig,
  urls,
  userId,
}) => {
  const [selectedChannels, setSelectedChannels] = useState(new Set())
  const cookieName = proDetailsPopupCookieName(userId)
  const [cookies, setCookie] = useCookies([cookieName])

  const initialStep = recommendedApplets?.length
    ? cookies[cookieName]
      ? Steps.RECOMMENDED_APPLETS
      : Steps.UPSELL
    : Steps.SERVICE_SELECTION
  const [step, setStep] = useState(initialStep)

  const channelSelected = moduleName => selectedChannels.has(moduleName)

  const toggleChannel = moduleName => {
    let newChannels = new Set(selectedChannels)
    if (channelSelected(moduleName)) {
      newChannels.delete(moduleName)
    } else {
      newChannels.add(moduleName)
    }
    setSelectedChannels(newChannels)
  }

  const onNext = async () => {
    const success = await submitChannels(Array.from(selectedChannels))
    if (success) {
      logIFTTTNextAction("statechange", {
        object_type: "user",
        location_type: "onboarding_selector",
        state: "submitted_onboarding",
      })
      setStep(Steps.UPSELL)
    }
  }

  const onSkipPro = async () => {
    setCookie(cookieName, true)
    setStep(Steps.RECOMMENDED_APPLETS)
  }

  const onTryProClick = async () => {
    setCookie(cookieName, true)
    logIFTTTNextAction("click", {
      object_type: "try_pro_button",
      location_type: "onboarding_selector",
    })

    setStep(Steps.RECOMMENDED_APPLETS)
    window.appendFlash(TrialSuccessMessageCopies.intermediate_pro, "success")
  }

  const onTrialFailedForExistingUser = async errorMessage => {
    if (errorMessage === "You already have a Pro subscription") {
      setCookie(cookieName, true)
      setStep(Steps.RECOMMENDED_APPLETS)
    }
  }

  switch (step) {
    case Steps.SERVICE_SELECTION:
      return (
        <ServiceSelection
          channels={channels}
          onNext={onNext}
          showFooter={!!selectedChannels.size}
          channelSelected={channelSelected}
          toggleChannel={toggleChannel}
        />
      )
    case Steps.UPSELL:
      return (
        <Upsell
          iftttSvgUrl={iftttSvgUrl}
          onSkip={onSkipPro}
          onTryProClick={onTryProClick}
          proBadgeSvgUrl={proBadgeSvgUrl}
          trialConfig={trialConfig}
          handleErrorFlow={onTrialFailedForExistingUser}
        />
      )
    case Steps.RECOMMENDED_APPLETS:
      return <RecommendedApplets applets={recommendedApplets} urls={urls} />
  }
}

OnboardingRecommendations.propTypes = {
  channels: PropTypes.array.isRequired,
  iftttSvgUrl: PropTypes.string.isRequired,
  proBadgeSvgUrl: PropTypes.string.isRequired,
  recommendedApplets: PropTypes.array,
  urls: PropTypes.shape({
    exploreUrl: PropTypes.string.isRequired,
  }),
  userId: PropTypes.string.isRequired,
  trialConfig: PropTypes.object.isRequired,
}

export default OnboardingRecommendations
